/**
 * MUI Plot Level Display component
 * 
 * This component shows plot level information at mouse position for acoustics and speakers
 */
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { getIsMeter, getScale, getDrawingBox } from 'reduxModules/ducks/ui';
import { distanceToString } from 'utilities/format';

interface PlotLevelDisplayProps {
  mousePosition: { x: number; y: number };
  plotData?: {
    level: number;
    type: string;
    frequency?: string;
    unit: string;
  };
  visible: boolean;
}

/**
 * MUI Plot Level Display component
 * 
 * @param props - Component props
 * @returns {JSX.Element} Plot level display component
 */
const MuiPlotLevelDisplay: React.FC<PlotLevelDisplayProps> = ({ mousePosition, plotData, visible }) => {
  const scale = useSelector(getScale);
  const isMeter = useSelector(getIsMeter);
  const { bbox } = useSelector(getDrawingBox);

  if (!visible || !plotData) return null;

  // Convert room coordinates to actual position
  const roomX = distanceToString((mousePosition.x - bbox.x0) / scale, isMeter);
  const roomY = distanceToString((mousePosition.y - bbox.y0) / scale, isMeter);

  return (
    <PlotLevelTooltip
      transform={`translate(${mousePosition.x + 10}, ${mousePosition.y - 10})`}
    >
      <PlotLevelBackground
        width="200"
        height="30"
        rx="4"
        ry="4"
      />
      <PlotLevelText x="10" y="20">
        {plotData.type}: {plotData.level > 0 ? '+' : ''}{plotData.level.toFixed(1)} {plotData.unit}
      </PlotLevelText>
    </PlotLevelTooltip>
  );
};

/**
 * Get color based on the level value and type
 */
const getColorForLevel = (level: number, type: string): string => {
  if (type === 'Acoustic Level') {
    // Green for good levels, yellow for moderate, red for poor
    if (level >= 70) return '#4CAF50'; // Green
    if (level >= 50) return '#FFC107'; // Yellow
    return '#F44336'; // Red
  } else if (type === 'SPL') {
    // Speaker level - different scale
    if (level >= 80) return '#4CAF50'; // Green
    if (level >= 60) return '#FFC107'; // Yellow
    return '#F44336'; // Red
  }
  return '#2196F3'; // Default blue
};

// Styled components using MUI styled API
const PlotLevelTooltip = styled('g')({
  pointerEvents: 'none',
  zIndex: 1000,
});

const PlotLevelBackground = styled('rect')({
  fill: '#333',
  stroke: '#666',
  strokeWidth: 1,
  opacity: 0.9,
});

const PlotLevelText = styled('text')({
  fill: '#fff',
  fontSize: '12px',
  fontFamily: 'Arial, sans-serif',
});

const PlotLevelIndicator = styled('circle')({
  stroke: '#fff',
  strokeWidth: 1,
});

export default MuiPlotLevelDisplay;
